import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  IconChevronLeft,
  IconChevronRight,
  IconLogout,
  IconMoreVertical,
  IconSearch,
} from '../../components/icons';
import { ShippingNotificationBell } from '../orders/ShippingNotificationBell';
import SettingsModal from '../settings/SettingsModal';

const SIDEBAR_COLLAPSED_KEY = 'crm_sidebar_collapsed';

function getInitials(name, email) {
  const source = (name || email || '').trim();

  if (!source) {
    return 'U';
  }

  const parts = source.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return source.slice(0, 2).toUpperCase();
}

function readCollapsedPreference() {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function DashboardShell({
  user,
  navItems,
  onSignOut,
  title,
  subtitle,
  children,
  settingsProps,
  notificationToken,
  collapsible = true,
}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsedPreference);
  const [navQuery, setNavQuery] = useState('');
  const navSearchRef = useRef(null);
  const roleLabel =
    user.roleLabel ||
    (user.role === 'super_admin'
      ? 'Super Admin'
      : user.role === 'workspace_user'
        ? 'User'
        : 'Admin');

  useEffect(() => {
    if (!collapsible) {
      return;
    }

    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // Ignore storage failures (private mode, quota, etc.)
    }
  }, [collapsed, collapsible]);

  useEffect(() => {
    if (collapsed) {
      setNavQuery('');
    }
  }, [collapsed]);

  const filteredNavItems = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter((item) => {
      const label = String(item.label || '').toLowerCase();
      const hint = String(item.hint || '').toLowerCase();
      const id = String(item.id || '').toLowerCase();
      return label.includes(q) || hint.includes(q) || id.includes(q);
    });
  }, [navItems, navQuery]);

  const handleConfirmLogout = () => {
    setLogoutOpen(false);
    onSignOut();
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => !prev);
  };

  const isRail = collapsible && collapsed;
  const pageGridClass = isRail
    ? 'crm-page-bg min-h-screen lg:grid lg:h-screen lg:grid-cols-[4.75rem_minmax(0,1fr)] lg:overflow-hidden'
    : 'crm-page-bg min-h-screen lg:grid lg:h-screen lg:grid-cols-[16.5rem_minmax(0,1fr)] lg:overflow-hidden';

  return (
    <>
      <div className={pageGridClass}>
        <aside
          className={[
            'crm-sidebar grid gap-5 p-4 lg:z-30 lg:h-full lg:max-h-full lg:grid-rows-[auto_auto_1fr_auto] lg:overflow-x-hidden lg:overflow-y-hidden lg:p-5',
            isRail ? 'lg:gap-4 lg:px-2.5 lg:py-4 lg:grid-rows-[auto_1fr_auto]' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            className={[
              'crm-sidebar-brand-block flex items-center gap-3',
              isRail ? 'lg:flex-col lg:gap-2' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span
              className="inline-grid h-10 w-10 shrink-0 place-items-center rounded-xl text-[11px] font-extrabold tracking-wide text-white shadow-[0_8px_20px_-10px_rgb(var(--crm-accent)_/_0.55)]"
              style={{
                background:
                  'linear-gradient(135deg, rgb(var(--crm-accent)) 0%, rgb(var(--crm-accent-hover)) 100%)',
              }}
            >
              PS
            </span>
            {!isRail ? (
              <div className="min-w-0 flex-1">
                <strong className="crm-sidebar-brand block truncate text-sm font-bold tracking-tight">
                  PandaSeller
                </strong>
                <span className="crm-sidebar-muted block truncate text-[11px]">Seller workspace</span>
              </div>
            ) : null}
            {collapsible ? (
              <button
                type="button"
                className={[
                  'hidden h-8 w-8 shrink-0 place-items-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white lg:inline-grid',
                  isRail ? 'lg:mx-auto' : 'ml-auto',
                ].join(' ')}
                onClick={toggleCollapsed}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                aria-expanded={!collapsed}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? (
                  <IconChevronRight className="h-4 w-4" />
                ) : (
                  <IconChevronLeft className="h-4 w-4" />
                )}
              </button>
            ) : null}
          </div>

          {!isRail ? (
            <label className="relative block">
              <span className="sr-only">Search navigation</span>
              <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/45" />
              <input
                ref={navSearchRef}
                type="search"
                value={navQuery}
                onChange={(event) => setNavQuery(event.target.value)}
                placeholder="Search menu…"
                className="w-full rounded-lg border border-white/12 bg-white/5 py-2 pl-8 pr-3 text-[13px] text-white/90 placeholder:text-white/40 outline-none transition focus:border-white/25 focus:bg-white/10"
                autoComplete="off"
              />
            </label>
          ) : null}

          <nav
            className={[
              'grid content-start gap-1 max-lg:auto-cols-[minmax(8rem,1fr)] max-lg:grid-flow-col max-lg:overflow-x-auto lg:min-h-0 lg:overflow-y-auto',
              isRail ? 'lg:justify-items-center' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label="Main navigation"
          >
            {filteredNavItems.length === 0 ? (
              <p className="px-3 py-2 text-[12px] text-white/50">No matching pages</p>
            ) : null}
            {filteredNavItems.map((item) => {
              const Icon = item.icon;
              const content = (
                <>
                  <span
                    className={[
                      'flex items-center gap-2.5 text-[13px] font-medium',
                      isRail ? 'lg:justify-center lg:gap-0' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {Icon ? <Icon className="h-[18px] w-[18px] shrink-0 opacity-90" /> : null}
                    <span className={isRail ? 'lg:hidden' : ''}>{item.label}</span>
                  </span>
                  {item.hint ? (
                    <span
                      className={[
                        'text-[10px] uppercase tracking-wide opacity-70',
                        isRail ? 'lg:hidden' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {item.hint}
                    </span>
                  ) : null}
                </>
              );

              const linkClass = ({ isActive }) =>
                [
                  'grid w-full gap-0.5 rounded-lg text-left transition',
                  isRail ? 'lg:place-items-center lg:px-2 lg:py-2.5' : 'px-3 py-2.5',
                  isActive ? 'crm-nav-link-active' : 'crm-nav-link',
                ].join(' ');

              if (item.disabled) {
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={[
                      'crm-nav-link grid w-full gap-0.5 rounded-lg text-left opacity-45',
                      isRail ? 'lg:place-items-center lg:px-2 lg:py-2.5' : 'px-3 py-2.5',
                    ].join(' ')}
                    disabled
                    title={item.label}
                  >
                    {content}
                  </button>
                );
              }

              if (item.to) {
                return (
                  <NavLink
                    key={item.id}
                    to={item.to}
                    className={linkClass}
                    title={isRail ? item.label : undefined}
                  >
                    {content}
                  </NavLink>
                );
              }

              return (
                <button
                  key={item.id}
                  type="button"
                  className={[
                    'grid w-full gap-0.5 rounded-lg text-left transition',
                    isRail ? 'lg:place-items-center lg:px-2 lg:py-2.5' : 'px-3 py-2.5',
                    item.active ? 'crm-nav-link-active' : 'crm-nav-link',
                  ].join(' ')}
                  onClick={item.onClick}
                  title={isRail ? item.label : undefined}
                >
                  {content}
                </button>
              );
            })}
          </nav>

          <div className="grid gap-3 border-t border-white/10 pt-4">
            <div
              className={[
                'flex items-center gap-3 rounded-lg px-1 py-1',
                isRail ? 'lg:flex-col lg:gap-2 lg:px-0' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span
                className="inline-grid h-9 w-9 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white"
                style={{
                  background:
                    'linear-gradient(135deg, rgb(var(--crm-accent)) 0%, rgb(var(--crm-accent-hover)) 100%)',
                }}
                title={user.name || user.email}
              >
                {getInitials(user.name, user.email)}
              </span>
              {!isRail ? (
                <div className="min-w-0 flex-1">
                  <strong className="crm-sidebar-brand block truncate text-sm font-medium">
                    {user.name || user.email}
                  </strong>
                  <span className="crm-sidebar-muted block truncate text-[11px]">{roleLabel}</span>
                </div>
              ) : null}
              <button
                type="button"
                className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-lg text-white/70 transition hover:bg-white/10 hover:text-white"
                onClick={() => setSettingsOpen(true)}
                aria-label="Open settings"
                title="Settings"
              >
                <IconMoreVertical className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              className={[
                'rounded-lg border border-white/12 bg-white/5 text-[13px] font-medium text-white/90 transition hover:bg-white/10',
                isRail
                  ? 'lg:inline-grid lg:h-9 lg:w-9 lg:place-items-center lg:border-0 lg:bg-transparent lg:p-0 lg:hover:bg-white/10'
                  : 'min-h-9',
              ].join(' ')}
              onClick={() => setLogoutOpen(true)}
              aria-label="Sign out"
              title="Sign out"
            >
              {isRail ? (
                <>
                  <IconLogout className="hidden h-4 w-4 lg:block" />
                  <span className="lg:hidden">Sign out</span>
                </>
              ) : (
                'Sign out'
              )}
            </button>
          </div>
        </aside>

        <div
          data-page-scroll="true"
          className="grid min-h-0 min-w-0 grid-rows-[auto_1fr] overflow-x-hidden lg:overflow-y-auto"
        >
          <header className="crm-header sticky top-0 z-20 flex h-16 min-h-[3.75rem] items-center justify-between gap-3 border-b px-4 max-sm:px-3 sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="min-w-0">
                <h1 className="truncate font-display text-base font-bold tracking-tight sm:text-lg">
                  {title}
                </h1>
                {subtitle ? (
                  <p className="crm-text-muted hidden max-w-2xl truncate text-xs leading-normal md:block">
                    {subtitle}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              {notificationToken ? <ShippingNotificationBell token={notificationToken} /> : null}
              <span
                className="inline-flex h-7 items-center rounded-full px-2.5 text-xs font-semibold"
                style={{
                  backgroundColor: 'rgb(var(--crm-accent-soft, var(--crm-page-bg)))',
                  color: 'rgb(var(--crm-accent-text))',
                }}
              >
                {roleLabel}
              </span>
              <time
                className="crm-text-muted hidden text-xs whitespace-nowrap lg:block"
                dateTime={new Date().toISOString().split('T')[0]}
              >
                {new Date().toLocaleDateString(undefined, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </time>
            </div>
          </header>

          <div className="crm-page-enter min-w-0 overflow-x-hidden px-4 py-4 max-sm:px-3 sm:px-5">{children}</div>
        </div>
      </div>

      {settingsProps ? (
        <SettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          user={settingsProps.user}
          token={settingsProps.token}
          onUserUpdate={settingsProps.onUserUpdate}
        />
      ) : null}

      <ConfirmDialog
        open={logoutOpen}
        title="Sign out"
        message="Do you really want to logout?"
        confirmLabel="Sign out"
        cancelLabel="Cancel"
        onConfirm={handleConfirmLogout}
        onCancel={() => setLogoutOpen(false)}
      />
    </>
  );
}

export default DashboardShell;
